import { v4 as uuidv4 } from "uuid";

// In-memory store for Copilot logs (per page session)
const LOGS_MEM: CopilotLogEntry[] = [];

export type CopilotLogKind =
  | "user-message"
  | "send-message-call"
  | "stream-event"
  | "stream-error"
  | "stream-finished"
  | "cancel-task-call"
  | "cancel-task-success"
  | "cancel-task-error";

export interface CopilotLogEntry {
  id: string;
  kind: CopilotLogKind;
  timeMs: number;
  isoTime: string;
  // Optional contextual fields to assist debugging
  documentId?: string;
  threadId?: string;
  taskId?: string;
  contextId?: string;
  // Arbitrary payload for the event
  payload?: unknown;
}

const nowTime = () => {
  const t = Date.now();
  return { timeMs: t, isoTime: new Date(t).toISOString() };
};

const appendLog = (
  entry: Omit<CopilotLogEntry, "id" | "timeMs" | "isoTime">
) => {
  if (process.env.REACT_ENV !== "staging") {
    return;
  }
  const time = nowTime();
  const full: CopilotLogEntry = {
    id: uuidv4(),
    ...time,
    ...entry,
  };
  LOGS_MEM.push(full);
};

export const logUserMessage = (params: {
  documentId: string;
  threadId: string;
  message: string;
}) => {
  appendLog({
    kind: "user-message",
    documentId: params.documentId,
    threadId: params.threadId,
    payload: { message: params.message },
  });
};

export const logSendMessageCall = (params: {
  documentId: string;
  threadId: string;
  message: string;
  contextId?: string;
  metadata?: Record<string, unknown>;
}) => {
  appendLog({
    kind: "send-message-call",
    documentId: params.documentId,
    threadId: params.threadId,
    contextId: params.contextId,
    payload: { message: params.message, metadata: params.metadata },
  });
};

export const logStreamEvent = (params: {
  documentId: string;
  threadId: string;
  event: unknown;
  taskId?: string;
  contextId?: string;
}) => {
  appendLog({
    kind: "stream-event",
    documentId: params.documentId,
    threadId: params.threadId,
    taskId: params.taskId,
    contextId: params.contextId,
    payload: params.event,
  });
};

export const logStreamError = (params: {
  documentId: string;
  threadId: string;
  errorMessage: string;
}) => {
  appendLog({
    kind: "stream-error",
    documentId: params.documentId,
    threadId: params.threadId,
    payload: { message: params.errorMessage },
  });
};

export const logStreamFinished = (params: {
  documentId: string;
  threadId: string;
}) => {
  appendLog({
    kind: "stream-finished",
    documentId: params.documentId,
    threadId: params.threadId,
  });
};

export const logCancelTaskCall = (params: { taskId: string }) => {
  appendLog({
    kind: "cancel-task-call",
    taskId: params.taskId,
  });
};

export const logCancelTaskSuccess = (params: { taskId: string }) => {
  appendLog({
    kind: "cancel-task-success",
    taskId: params.taskId,
  });
};

export const logCancelTaskError = (params: {
  taskId: string;
  errorMessage: string;
}) => {
  appendLog({
    kind: "cancel-task-error",
    taskId: params.taskId,
    payload: { message: params.errorMessage },
  });
};

export const getCopilotLogs = (): CopilotLogEntry[] => {
  return [...LOGS_MEM].sort((a, b) => a.timeMs - b.timeMs);
};

export const downloadCopilotLogs = (filename?: string) => {
  if (process.env.REACT_ENV !== "staging") {
    return;
  }
  const logs = getCopilotLogs();
  const json = JSON.stringify(logs, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const ts = new Date();
  const pad = (n: number) => n.toString().padStart(2, "0");
  const name =
    filename ||
    `ai-chat-logs-${ts.getFullYear()}${pad(ts.getMonth() + 1)}${pad(ts.getDate())}-${pad(
      ts.getHours()
    )}${pad(ts.getMinutes())}${pad(ts.getSeconds())}.json`;
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
